'use client';

import { useState, useMemo } from 'react';
import { CareerPath } from '@/lib/careerPaths';
import { getCareerProgression, CareerProgression, CareerLevel } from '@/lib/careerProgressionData';
import styles from './CareerProgressionTimeline.module.css';

interface CareerProgressionTimelineProps {
  career: CareerPath;
}

export default function CareerProgressionTimeline({
  career
}: CareerProgressionTimelineProps) {
  const [selectedLevel, setSelectedLevel] = useState<number>(0);
  const [showAlternatives, setShowAlternatives] = useState(false);

  // Get career progression data
  const progression: CareerProgression = useMemo(() => {
    return getCareerProgression(career);
  }, [career]);

  const selectedLevelData = progression.levels[selectedLevel];

  const formatSalary = (amount: number): string => {
    return `$${Math.round(amount / 1000)}K`;
  };

  const getProgressWidth = (index: number): string => {
    // Calculate progress through career (0%, 33%, 66%, 100%)
    const percentage = (index / (progression.levels.length - 1)) * 100;
    return `${percentage}%`;
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerIcon}>📈</div>
        <div className={styles.headerContent}>
          <h3>Career Progression Timeline</h3>
          <p>Your path from entry-level to leadership as a {career.title}</p>
        </div>
      </div>

      {/* Timeline Visual */}
      <div className={styles.timelineContainer}>
        <div className={styles.timelineTrack}>
          <div
            className={styles.timelineProgress}
            style={{ width: getProgressWidth(selectedLevel) }}
          />
        </div>
        <div className={styles.timelineNodes}>
          {progression.levels.map((level, index) => (
            <button
              key={index}
              className={`${styles.timelineNode} ${index === selectedLevel ? styles.activeNode : ''} ${index < selectedLevel ? styles.passedNode : ''}`}
              onClick={() => setSelectedLevel(index)}
              aria-label={`View ${level.title} details`}
            >
              <span className={styles.nodeCircle}>
                {index < selectedLevel ? '✓' : index + 1}
              </span>
              <span className={styles.nodeLabel}>{level.title}</span>
              <span className={styles.nodeYears}>{level.yearsExperience}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Salary Progression Chart */}
      <div className={styles.salaryChart}>
        <h4>Salary Progression</h4>
        <div className={styles.salaryBars}>
          {progression.levels.map((level, index) => {
            const maxSalary = Math.max(...progression.levels.map(l => l.salaryRange.max));
            const barHeight = (level.salaryRange.max / maxSalary) * 100;

            return (
              <div
                key={index}
                className={`${styles.salaryBar} ${index === selectedLevel ? styles.activeSalaryBar : ''}`}
                onClick={() => setSelectedLevel(index)}
              >
                <div className={styles.salaryBarFill} style={{ height: `${barHeight}%` }}>
                  <span className={styles.salaryAmount}>
                    {formatSalary(level.salaryRange.min)}-{formatSalary(level.salaryRange.max)}
                  </span>
                </div>
                <span className={styles.salaryLabel}>{level.title.split(' ')[0]}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Selected Level Details */}
      <div className={styles.levelDetails}>
        <div className={styles.levelHeader}>
          <div className={styles.levelBadge}>Level {selectedLevel + 1}</div>
          <h4>{selectedLevelData.title}</h4>
          <div className={styles.levelMeta}>
            <span className={styles.levelYears}>{selectedLevelData.yearsExperience}</span>
            <span className={styles.levelSalary}>
              {formatSalary(selectedLevelData.salaryRange.min)} - {formatSalary(selectedLevelData.salaryRange.max)}
            </span>
          </div>
        </div>

        {/* Time to Reach */}
        <div className={styles.timeToReach}>
          <span className={styles.clockIcon}>⏱️</span>
          <span>Typical time to reach: <strong>{selectedLevelData.typicalTimeToReach}</strong></span>
        </div>

        {/* Key Responsibilities */}
        <div className={styles.detailSection}>
          <h5>Key Responsibilities</h5>
          <ul className={styles.responsibilityList}>
            {selectedLevelData.keyResponsibilities.map((resp, idx) => (
              <li key={idx}>{resp}</li>
            ))}
          </ul>
        </div>

        {/* Skills Required */}
        <div className={styles.detailSection}>
          <h5>Skills Required</h5>
          <div className={styles.skillTags}>
            {selectedLevelData.skillsRequired.map((skill, idx) => (
              <span key={idx} className={styles.skillTag}>{skill}</span>
            ))}
          </div>
        </div>

        {/* Certifications */}
        {selectedLevelData.certifications && selectedLevelData.certifications.length > 0 && (
          <div className={styles.detailSection}>
            <h5>Recommended Certifications</h5>
            <div className={styles.certTags}>
              {selectedLevelData.certifications.map((cert, idx) => (
                <span key={idx} className={styles.certTag}>🎓 {cert}</span>
              ))}
            </div>
          </div>
        )}

        {/* Navigation Arrows */}
        <div className={styles.levelNavigation}>
          <button
            className={styles.navButton}
            onClick={() => setSelectedLevel(Math.max(0, selectedLevel - 1))}
            disabled={selectedLevel === 0}
          >
            ← Previous Level
          </button>
          <button
            className={styles.navButton}
            onClick={() => setSelectedLevel(Math.min(progression.levels.length - 1, selectedLevel + 1))}
            disabled={selectedLevel === progression.levels.length - 1}
          >
            Next Level →
          </button>
        </div>
      </div>

      {/* Key Milestones */}
      <div className={styles.milestonesSection}>
        <h4>Key Career Milestones</h4>
        <div className={styles.milestonesList}>
          {progression.keyMilestones.map((milestone, idx) => (
            <div key={idx} className={styles.milestoneCard}>
              <span className={styles.milestoneIcon}>{milestone.icon}</span>
              <div className={styles.milestoneContent}>
                <span className={styles.milestoneTiming}>{milestone.timing}</span>
                <span className={styles.milestoneTitle}>{milestone.title}</span>
                <span className={styles.milestoneDescription}>{milestone.description}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Alternative Paths */}
      <div className={styles.alternativeSection}>
        <button
          className={styles.alternativeToggle}
          onClick={() => setShowAlternatives(!showAlternatives)}
        >
          <span>🔀 Alternative Career Paths</span>
          <span className={styles.toggleIcon}>{showAlternatives ? '−' : '+'}</span>
        </button>

        {showAlternatives && (
          <div className={styles.alternativesList}>
            {progression.alternativePaths.map((path, idx) => (
              <div key={idx} className={styles.alternativeCard}>
                <div className={styles.alternativeHeader}>
                  <span className={styles.alternativeTitle}>{path.title}</span>
                  <span className={styles.alternativeFrom}>
                    Branch from: {progression.levels[path.fromLevel]?.title || 'Mid-Level'}
                  </span>
                </div>
                <p className={styles.alternativeDescription}>{path.description}</p>
                <div className={styles.alternativeRequirements}>
                  <span className={styles.requirementsLabel}>Requirements:</span>
                  <div className={styles.requirementTags}>
                    {path.requirements.map((req, ridx) => (
                      <span key={ridx} className={styles.requirementTag}>{req}</span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Growth Tips */}
      <div className={styles.growthTipsSection}>
        <h4>💡 Growth Tips</h4>
        <ul className={styles.growthTipsList}>
          {progression.growthTips.map((tip, idx) => (
            <li key={idx}>{tip}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}
